/*
 Custom Cart Callbacks for Rebuy Smart Cart
 
 Handles dynamic UI updates for cart line items when discount codes are applied or removed.
 
 Key Features:
 - Tracks original cart item state (prices, sale status)
 - Calculates discounted prices using proportional allocation (RebuyUtils)
 - Updates DOM to display discounted prices with proper formatting
 - Reverts items to original state when discounts removed
 - Responds to cart events: change, ready, show, discount applied/removed
 
 Discount Support:
 - Manual codes (cart-level preview) - full price management
 - URL codes (checkout-level) - defers to Shopify, price management for > 1 items only
 - AUTO discounts (checkout-level) - defers to Shopify, price management for > 1 items only
 - Gift codes, manual or URL (0% and % off) - works with AUTO multiples
 - Multiples promos (3for2/4for3) - minimal processing, handled by MultiplesPromoHandler
 
 Technical Notes:
 - Uses signature tracking to avoid redundant DOM updates
 - Shopify-managed mode: skips processing when AUTO/URL multiples active
 - Combination scenarios: AUTO multiples + manual percentage discount
 - Relies on window.RebuyUtils for cart data and discount allocation
 
 Last Updated: October 2025 (Singles Day AUTO/URL support)
 */

window.RebuyCartCallbacks = (function () {
  // Private variables
  const originalStates = new Map();
  const lastProcessedState = {
    discountSignature: null,
    itemsSignature: null,
    lastUpdate: 0,
  };
  let debug = false;

  function log(...args) {
    if (debug) {
      console.log('[RebuyCartCallbacks]', ...args);
    }
  }

  // Check if a code is a valid multi-buy promo code
  function isValidMultiBuyCode(code) {
    if (!code) return false;
    const activeCodes = window.activeMultiBuyCodes.getActiveCodes();

    if (!Array.isArray(activeCodes)) return false;

    return activeCodes.includes(code.trim().toUpperCase());
  }

  function shouldUpdateSubtotal(cart) {
    // Check 1: Auto discount present
    const autoDiscountData = window.RebuyUtils.detectAutomaticBuyXGetY(cart);
    const hasAutoDiscount = autoDiscountData.isActive;

    // Check 2: Manual discount badge visible AND it's not a Multiples code AND it's not a 0% gift code
    const discountBadge = document.querySelector('.rebuy-cart__discount-tag');
    const manualCode =
      discountBadge && discountBadge.offsetParent !== null
        ? discountBadge.textContent.trim().split(' ')[0].toUpperCase()
        : '';

    // Exclude 0% gift codes from subtotal update logic, this was causing a weird minus bus
    const isZeroPercentGiftCode =
      manualCode &&
      window.FreeGiftHandler &&
      window.FreeGiftHandler.isValidGiftCode(manualCode) &&
      window.RebuyUtils.getActiveDiscount().value === 0;
    
    const isMultiplesCode = manualCode && window.activeMultiBuyCodes?.getActiveCodes()?.includes(manualCode);

    const hasValidManualCode = manualCode && !isZeroPercentGiftCode && !isMultiplesCode;

    log('[shouldUpdateSubtotal]', {
      hasAutoDiscount,
      hasValidManualCode,
      manualCode,
      isZeroPercentGiftCode,
      isMultiplesCode,
    });

    return hasAutoDiscount && hasValidManualCode;
  }

  function updateCustomSubtotal() {
    let total = 0;

    // Get ONLY the actual prices being paid (not compare-at strikethrough prices)
    document
      .querySelectorAll('.rebuy-cart__flyout-item-price')
      .forEach(priceContainer => {
        // Priority: sale price > regular price (never compare-at)
        let priceSpan = priceContainer.querySelector('.rebuy-money.sale span:last-child');
        
        if (!priceSpan) {
          priceSpan = priceContainer.querySelector('.rebuy-money:not(.compare-at):not(.sale) span:last-child');
        }

        if (!priceSpan) return;

        const price = parseFloat(priceSpan.textContent.replace('€', '').replace(',', '.'));

        // Filter out items that are made free by MultiplesPromoHandler
        const itemElement = priceContainer.closest('[data-item-id]');
        const IsMultiplesPromoFree = itemElement?.hasAttribute('data-multiples-promo-free');

        log('[updateCustomSubtotal] Found item:', {
          price,
          itemId: itemElement?.getAttribute('data-item-id'),
          IsMultiplesPromoFree,
        });

        // Only add to total if it's not a Multiples free item
        if (!IsMultiplesPromoFree) {
          total += price;
          log('Added to total:', price);
        } else {
          log('Skipped Multiples free item:', price);
        }
      });

    log('Total calculated:', total);

    const formattedTotal = total.toFixed(2).replace('.', ',') + '€';
    const subtotalSpan = document.querySelector(
      '.rebuy-cart__flyout-subtotal-final-amount span:last-child'
    );

    if (subtotalSpan) {
      log('Old subtotal:', subtotalSpan.textContent);
      subtotalSpan.textContent = formattedTotal;
      log('New subtotal:', formattedTotal);
    } else {
      log('Subtotal span not found');
    }
  }


  function shouldProcessCart(cart) {
    const currentDiscount = window.RebuyUtils.getActiveDiscount();
    const itemsSignature = cart.items
      .map(item => `${item.id}:${item.quantity}:${item.discounted_price || 'none'}`)
      .join('|');

    // Handle new discount object structure
    const hasNoDiscount = currentDiscount.type === 'none' || currentDiscount.hasDiscount === false;
    const hasValidDiscount =
      currentDiscount &&
      typeof currentDiscount === 'object' &&
      currentDiscount.isValid !== false &&
      !hasNoDiscount;
    const hasInvalidDiscount =
      currentDiscount && typeof currentDiscount === 'object' && currentDiscount.isValid === false;

    // Enhanced discount signature generation
    let discountSignature;
    if (hasNoDiscount) {
      discountSignature =
        originalStates.size > 0 ? 'no_discount:had_previous' : 'no_discount:clean';
    } else if (hasInvalidDiscount) {
      discountSignature = `${currentDiscount.value}:${currentDiscount.type}:${currentDiscount.source}:invalid`;
    } else if (hasValidDiscount) {
      discountSignature = `${currentDiscount.value}:${currentDiscount.type}:${currentDiscount.source}:valid`;
    } else {
      // Fallback for any unexpected structure
      discountSignature = String(currentDiscount);
    }

    const autoDiscountData = window.RebuyUtils.detectAutomaticBuyXGetY(cart);
    const hasAutoMultiples = autoDiscountData.isActive;

    const isURLDiscount =
      currentDiscount &&
      typeof currentDiscount === 'object' &&
      currentDiscount.source === 'checkout_level';
    const discountTag = document.querySelector('.rebuy-cart__discount-tag');
    const hasManualBadge = discountTag && discountTag.offsetParent !== null;
    const isCombinationScenario = hasAutoMultiples && (isURLDiscount || hasManualBadge);

    const hasChanges =
      lastProcessedState.discountSignature !== discountSignature ||
      lastProcessedState.itemsSignature !== itemsSignature ||
      Date.now() - lastProcessedState.lastUpdate > 5000 ||
      isCombinationScenario;

    if (hasChanges) {
      lastProcessedState.discountSignature = discountSignature;
      lastProcessedState.itemsSignature = itemsSignature;
      lastProcessedState.lastUpdate = Date.now();
      return true;
    }

    return false;
  }

  function calculatePrices(cartItem, discountPercent = 0, discountAllocation = null) {
    const originalState = originalStates.get(cartItem.id);
    const isSaleItem = originalState
      ? originalState.isSaleItem
      : cartItem.product?.selected_variant?.compare_at_price &&
        parseFloat(cartItem.product.selected_variant.compare_at_price) >
          parseFloat(cartItem.price) / 100
      ? true
      : false;

    const salePricePerUnit = originalState
      ? originalState.basePrice
      : parseFloat(cartItem.price) / 100;

    const fullPricePerUnit = isSaleItem
      ? cartItem.product?.selected_variant?.compare_at_price || null
      : null;

    let totalSalePrice = salePricePerUnit * cartItem.quantity;
    let totalCompareAtPrice = fullPricePerUnit ? fullPricePerUnit * cartItem.quantity : null;

    if (discountPercent > 0 && discountAllocation) {
      totalSalePrice = discountAllocation.discountedPrice;
    } else if (discountPercent > 0) {
      totalSalePrice = totalSalePrice * (1 - discountPercent / 100);
      totalSalePrice = Math.round(totalSalePrice * 100) / 100;
    }

    return {
      totalSalePrice: totalSalePrice.toFixed(2).replace('.', ','),
      totalCompareAtPrice: totalCompareAtPrice
        ? totalCompareAtPrice.toFixed(2).replace('.', ',')
        : null,
      hasDiscount: discountPercent > 0,
      isSaleItem: isSaleItem,
    };
  }

  function saveOriginalState(cartItem, itemElement, isSaleItem = null) {
    if (!originalStates.has(cartItem.id)) {
      const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
      if (!priceContainer) {
        console.error(`[ERROR] No price container found for item ${cartItem.id}`);
        return;
      }

      const itemIsSale =
        isSaleItem !== null
          ? isSaleItem
          : priceContainer.querySelector('.rebuy-money.compare-at') !== null ||
            priceContainer.querySelector('.rebuy-money.sale') !== null;

      originalStates.set(cartItem.id, {
        html: priceContainer.innerHTML,
        basePrice: parseFloat(cartItem.price) / 100,
        isSaleItem: itemIsSale,
      });
    }
  }

  function updateCartItemDOM(
    cartItem,
    totalSalePrice,
    totalCompareAtPrice,
    hasDiscount,
    isSaleItem
  ) {
    const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
    if (!itemElement) return;

    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceContainer) return;

    saveOriginalState(cartItem, itemElement, isSaleItem);

    const numericSalePrice =
      typeof totalSalePrice === 'string'
        ? parseFloat(totalSalePrice.replace(',', '.'))
        : totalSalePrice;
    const numericComparePrice =
      totalCompareAtPrice && typeof totalCompareAtPrice === 'string'
        ? parseFloat(totalCompareAtPrice.replace(',', '.'))
        : totalCompareAtPrice;

    const formattedSalePrice = numericSalePrice.toFixed(2).replace('.', ',');
    const formattedComparePrice = numericComparePrice
      ? numericComparePrice.toFixed(2).replace('.', ',')
      : null;

    if (hasDiscount) {
      let compareAtPrice;
      if (isSaleItem) {
        const original = originalStates.get(cartItem.id);
        compareAtPrice = (original.basePrice * cartItem.quantity).toFixed(2).replace('.', ',');
      } else {
        compareAtPrice = ((parseFloat(cartItem.price) / 100) * cartItem.quantity)
          .toFixed(2)
          .replace('.', ',');
      }

      priceContainer.innerHTML = `
       <div>
         <span class="rebuy-money sale">
           <span class="sr-only">${window.themeVariables.strings.discountedPrice}</span>
           <span>${formattedSalePrice}€</span>
         </span>
         <span class="rebuy-money compare-at">
           <span class="sr-only">${window.themeVariables.strings.originalPrice}</span>
           <span>${compareAtPrice}€</span>
         </span>
       </div>
     `;
    } else if (isSaleItem) {
      priceContainer.innerHTML = `
       <div>
         <span class="rebuy-money sale">
           <span class="sr-only">${window.themeVariables.strings.salePrice}</span>
           <span>${formattedSalePrice}€</span>
         </span>
         <span class="rebuy-money compare-at">
           <span class="sr-only">${window.themeVariables.strings.comparePrice}</span>
           <span>${formattedComparePrice}€</span>
         </span>
       </div>
     `;
    } else {
      priceContainer.innerHTML = `
       <span class="rebuy-money">
         <span class="sr-only">${window.themeVariables.strings.price}</span>
         <span>${formattedSalePrice}€</span>
       </span>
     `;
    }
  }

  function revertItemToOriginal(cartItem, isSaleItem) {
    const itemId = cartItem.id;
    const original = originalStates.get(itemId);
    if (original) {
      const itemElement = document.querySelector(`[data-item-id="${itemId}"]`);
      if (!itemElement) return;

      const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
      if (priceContainer) {
        const currentQty = cartItem.quantity;
        const itemIsSale = isSaleItem !== undefined ? isSaleItem : original.isSaleItem;

        if (itemIsSale) {
          const salePrice = (original.basePrice * currentQty).toFixed(2).replace('.', ',');
          const compareAtPrice = cartItem.product?.selected_variant?.compare_at_price
            ? (cartItem.product.selected_variant.compare_at_price * currentQty)
                .toFixed(2)
                .replace('.', ',')
            : null;

          if (compareAtPrice) {
            priceContainer.innerHTML = `
             <div>
               <span class="rebuy-money sale">
                 <span class="sr-only">${window.themeVariables.strings.salePrice}</span>
                 <span>${salePrice}€</span>
               </span>
               <span class="rebuy-money compare-at">
                 <span class="sr-only">${window.themeVariables.strings.comparePrice}</span>
                 <span>${compareAtPrice}€</span>
               </span>
             </div>
           `;
          } else {
            priceContainer.innerHTML = `
             <span class="rebuy-money">
               <span class="sr-only">${window.themeVariables.strings.price}</span>
               <span>${salePrice}€</span>
             </span>
           `;
          }
        } else {
          const price = (original.basePrice * currentQty).toFixed(2).replace('.', ',');
          priceContainer.innerHTML = `
           <span class="rebuy-money">
             <span class="sr-only">${window.themeVariables.strings.price}</span>
             <span>${price}€</span>
           </span>
         `;
        }
      } else {
        console.error(`[ERROR] No price container found for Item ID: ${itemId}`);
      }
    }
  }

  async function processCartItems(cart) {
    if (!cart.items.length) return;

    if (!shouldProcessCart(cart)) {
      log('[RebuyCartCallbacks] No changes detected, skipping DOM updates');
      return;
    }

    const discountData = window.RebuyUtils.getActiveDiscount();
    const hasValidDiscountBefore =
      lastProcessedState.discountSignature &&
      lastProcessedState.discountSignature !== 'no_discount:invalid' &&
      !lastProcessedState.discountSignature.includes('no_discount');

    // Handle both invalid discounts and removed discounts
    const noDiscountAtAll = discountData.type === 'none' || discountData.hasDiscount === false;
    const discountExistsButInvalid =
      discountData && typeof discountData === 'object' && discountData.isValid === false;
    const shouldRevert = (noDiscountAtAll && hasValidDiscountBefore) || discountExistsButInvalid;

    if (shouldRevert && originalStates.size > 0) {
      if (discountExistsButInvalid) {
        log('[ProcessCart] Discount invalid due to conditions (MOV, etc.) - reverting UI');
      } else {
        log('[ProcessCart] Discount removed - reverting UI');
      }

      // Revert all items to original state
      cart.items.forEach(cartItem => {
        if (originalStates.has(cartItem.id)) {
          revertItemToOriginal(cartItem);
        }
      });
      originalStates.clear(); // Clear tracking
      return; // Exit early
    }

    log('[RebuyCartCallbacks] Processing cart changes...');
    log(
      'Items signature:',
      cart.items
        .map(item => `${item.id}:${item.quantity}:${item.discounted_price || 'none'}`)
        .join('|')
    );
    log('Discount signature:', lastProcessedState.discountSignature);

    try {
      const autoDiscountData = window.RebuyUtils.detectAutomaticBuyXGetY(cart);
      const hasAutoMultiples = autoDiscountData.isActive;
      const discountData = window.RebuyUtils.getActiveDiscount();
      const isURLDiscount =
        discountData &&
        typeof discountData === 'object' &&
        discountData.source === 'checkout_level';

      const discountTag = document.querySelector('.rebuy-cart__discount-tag');
      const manualCode =
        discountTag && discountTag.offsetParent !== null
          ? discountTag.textContent.trim().split(' ')[0].toUpperCase()
          : '';

      const isMultibuyPromoCode = manualCode && isValidMultiBuyCode(manualCode);
      if (isMultibuyPromoCode) {
        log('[RebuyCartCallbacks] Multiples promo code detected');

        // Check if this is manual vs URL/auto
        const hasDiscountCookie = document.cookie.includes('discount_code=');

        if (!hasDiscountCookie) {
          // Manual multiples code - process non-discounted items for quantity updates
          log('[RebuyCartCallbacks] Manual multiples code - processing non-discounted items only');

          cart.items.forEach(cartItem => {
            const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
            const isDiscountedByMultiples = itemElement?.hasAttribute('data-multiples-promo-free');

            if (!isDiscountedByMultiples) {
              // Process this item for quantity updates
              const isSaleItem =
                cartItem.product?.selected_variant?.compare_at_price &&
                parseFloat(cartItem.product.selected_variant.compare_at_price) >
                  parseFloat(cartItem.price) / 100;

              if (originalStates.has(cartItem.id)) {
                revertItemToOriginal(cartItem, isSaleItem);
              } else {
                const { totalSalePrice, totalCompareAtPrice } = calculatePrices(cartItem);
                updateCartItemDOM(
                  cartItem,
                  totalSalePrice,
                  isSaleItem
                    ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                        .toFixed(2)
                        .replace('.', ',')
                    : null,
                  false,
                  isSaleItem
                );
              }
            }
          });

          return;
        } else {
          // URL/Auto multiples - full deferral to Shopify
          log('[RebuyCartCallbacks] URL multiples code - full deferral to MultiplesPromoHandler');
          return;
        }
      }

      // NEW: Enhanced detection for manual codes
      const isZeroPercentGiftCode =
        manualCode &&
        window.FreeGiftHandler &&
        window.FreeGiftHandler.isValidGiftCode(manualCode) &&
        discountData &&
        discountData.value === 0;

      const isManualDiscountCode = manualCode && !isZeroPercentGiftCode;
      const hasManualCode = isURLDiscount || isManualDiscountCode;

      log('[RebuyCartCallbacks] Detection:', {
        isURLDiscount,
        isManualDiscountCode,
        isZeroPercentGiftCode,
        hasManualCode,
        discountData,
      });

      if (hasAutoMultiples) {
        log(
          '[RebuyCartCallbacks] Automatic Multiples detected, checking for manual code combination'
        );

        if (hasManualCode) {
          // COMBINATION: Auto Multiples + Manual percentage/fixed discount (NOT 0% gift codes)
          log('[RebuyCartCallbacks] Combination discount: Auto Multiples + Manual discount code');

          let currentDiscount, discountType;
          if (typeof discountData === 'object' && discountData !== null) {
            currentDiscount = discountData.value;
            discountType = discountData.type;
          } else {
            currentDiscount = discountData;
            discountType = 'percentage';
          }

          const discountPercent = Math.abs(currentDiscount);

          log('[DEBUG COMBINATION DISCOUNT DATA]', {
            currentDiscount,
            discountPercent,
            discountData,
            discountType,
            manualCode: manualCode,
            willSkip: discountPercent === 0,
          });

          // Skip 0% codes in combination to prevent negative numbers
          if (discountPercent === 0) {
            log('[COMBO DEBUG] Zero-value discount code - no manual discount to apply');
            return;
          }

          if (currentDiscount < 0) {
            // Apply manual discount only to non-free items
            const nonFreeItems = cart.items.filter(
              item =>
                !(item.discounted_price !== undefined && parseInt(item.discounted_price) === 0)
            );

            if (nonFreeItems.length > 0) {
              let allocations;
              if (discountType === 'fixed_amount') {
                const result = window.RebuyUtils.allocateFixedDiscountsAcrossItems(
                  nonFreeItems,
                  discountPercent
                );
                allocations = result.allocations;
              } else {
                const result = window.RebuyUtils.allocateDiscountAcrossItems(
                  nonFreeItems,
                  discountPercent
                );
                allocations = result.allocations;
              }

              nonFreeItems.forEach(cartItem => {
                const allocation = allocations.get(cartItem.id);
                if (!allocation) return;

                const isSaleItem =
                  cartItem.product?.selected_variant?.compare_at_price &&
                  parseFloat(cartItem.product.selected_variant.compare_at_price) >
                    parseFloat(cartItem.price) / 100;

                const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
                if (itemElement) {
                  saveOriginalState(cartItem, itemElement, isSaleItem);
                }

                const discountedPriceDisplay = allocation.discountedPrice
                  .toFixed(2)
                  .replace('.', ',');
                const compareAtPrice = allocation.originalPrice.toFixed(2).replace('.', ',');

                updateCartItemDOM(
                  cartItem,
                  discountedPriceDisplay,
                  compareAtPrice,
                  true,
                  isSaleItem
                );
              });
            }
          }

          // Update subtotal if needed for combination scenario (but NOT for 0% gift codes)
          if (shouldUpdateSubtotal(cart)) {
            log('[ProcessCart] Safe combination scenario detected - updating subtotal');
            setTimeout(() => updateCustomSubtotal(), 300);
          }
        } else {
          // Auto Multiples ONLY (including with 0% gift codes)
          log('[RebuyCartCallbacks] Auto Multiples only - MultiplesPromoHandler manages free items');

          cart.items.forEach(cartItem => {
            // Skip free items (handled by MultiplesPromoHandler)
            if (
              cartItem.discounted_price !== undefined &&
              parseInt(cartItem.discounted_price) === 0
            ) {
              return;
            }

            // Skip items being handled by MultiplesPromoHandler
            const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
            if (itemElement && itemElement.hasAttribute('data-multiples-promo-free')) {
              return;
            }

            // Display regular prices for non-free items
            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            if (originalStates.has(cartItem.id)) {
              revertItemToOriginal(cartItem, isSaleItem);
            } else {
              const { totalSalePrice, totalCompareAtPrice } = calculatePrices(cartItem);
              updateCartItemDOM(
                cartItem,
                totalSalePrice,
                isSaleItem
                  ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                      .toFixed(2)
                      .replace('.', ',')
                  : null,
                false,
                isSaleItem
              );
            }
          });
          // NEW: Update subtotal when reverting from combo to AUTO-only
          if (originalStates.size > 0) {
            log('[ProcessCart] Reverted from combo to AUTO-only - updating subtotal');
            originalStates.clear(); // Clear the saved states
            setTimeout(() => updateCustomSubtotal(), 300);
          }
        }
        return; // Exit early for all auto Multiples scenarios
      }

      // MANUAL-ONLY DISCOUNT SCENARIOS
      log(
        '[RebuyCartCallbacks] No auto Multiples, processing manual discounts or quantity-only updates'
      );

      let currentDiscount, discountType;
      if (typeof discountData === 'object' && discountData !== null) {
        currentDiscount = discountData.value;
        discountType = discountData.type;
      } else {
        currentDiscount = discountData;
        discountType = 'percentage';
      }

      const discountPercent = Math.abs(currentDiscount);

      // Check if we have actual discount codes
      const hasDiscountCode = manualCode && manualCode.length > 0;
      const hasURLDiscount =
        discountData &&
        typeof discountData === 'object' &&
        discountData.source === 'checkout_level';
      const hasAnyDiscountCode = hasDiscountCode || hasURLDiscount;

      // Check for manual multi-promo code
      const isMultiBuyCode = window.activeMultiBuyCodes?.isValidCode
        ? window.activeMultiBuyCodes.isValidCode(manualCode)
        : false;

      if (isMultiBuyCode) {
        const config = window.activeMultiBuyCodes.getPromoConfig(manualCode);
        log(`[RebuyCartCallbacks] Manual ${config.label} code active, using minimal processing`);

        cart.items.forEach(cartItem => {
          try {
            if (parseInt(cartItem.price) === 0) return;

            const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
            if (itemElement && itemElement.hasAttribute('data-multiples-promo-free')) {
              return; // Skip items managed by MultiplesPromoHandler
            }

            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            if (originalStates.has(cartItem.id)) {
              revertItemToOriginal(cartItem, isSaleItem);
            } else {
              const { totalSalePrice, totalCompareAtPrice } = calculatePrices(cartItem);
              updateCartItemDOM(
                cartItem,
                totalSalePrice,
                isSaleItem
                  ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                      .toFixed(2)
                      .replace('.', ',')
                  : null,
                false,
                isSaleItem
              );
            }
          } catch (error) {
            console.error(`Error processing cart item ${cartItem.id}:`, error);
          }
        });

        return; // Exit early for manual multi-buy
      }

      // Get exact subtotal for single-item carts
      let exactSubtotal = null;
      if (cart.items.length === 1) {
        const subtotalElement = document.querySelector('.rebuy-cart__flyout-subtotal-final-amount');
        if (subtotalElement) {
          const subtotalText = subtotalElement.textContent.trim();
          const subtotalMatch = subtotalText.match(/[\d.,]+/);
          if (subtotalMatch) {
            const numericText = subtotalMatch[0];
            const parsableText = numericText.replace(',', '.');
            exactSubtotal = parseFloat(parsableText);
          }
        }
      }

      // Skip 0% codes for discount allocation only (not quantity updates)
      if (discountPercent === 0 && hasAnyDiscountCode) {
        log(
          '[RebuyCartCallbacks] Zero-value code - skipping discount allocation, proceeding to quantity updates'
        );
        // Fall through to quantity-only logic below instead of returning
      } else if (
        currentDiscount < 0 &&
        discountData.type !== 'none' &&
        discountData.hasDiscount !== false &&
        discountData.isValid !== false
      ) {
        // REGULAR DISCOUNT CODES
        log('[RebuyCartCallbacks] Regular discount active, applying to all items');

        let allocations;
        if (discountType === 'fixed_amount') {
          const result = window.RebuyUtils.allocateFixedDiscountsAcrossItems(
            cart.items,
            discountPercent
          );
          allocations = result.allocations;
        } else {
          const result = window.RebuyUtils.allocateDiscountAcrossItems(cart.items, discountPercent);
          allocations = result.allocations;
        }

        cart.items.forEach(cartItem => {
          try {
            if (parseInt(cartItem.price) === 0) return;

            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
            if (itemElement) {
              saveOriginalState(cartItem, itemElement, isSaleItem);
            }

            const allocation = allocations.get(cartItem.id);
            if (!allocation) {
              console.error(`[ERROR] No discount allocation found for item ${cartItem.id}`);
              return;
            }

            const original = originalStates.get(cartItem.id);

            // Calculate normally first
            let discountedPriceDisplay = allocation.discountedPrice.toFixed(2).replace('.', ',');

            // Simple rounding fix for single-item discounted carts
            if (cart.items.length === 1 && exactSubtotal !== null) {
              const lineItemPrice = allocation.discountedPrice;
              const cartSubtotal = exactSubtotal;
              const difference = Math.abs(lineItemPrice - cartSubtotal);

              log(
                '[ROUNDING FIX DEBUG] Line item:',
                lineItemPrice,
                'Cart subtotal:',
                cartSubtotal,
                'Difference:',
                difference
              );

              // If there's a small rounding difference, use the cart subtotal for consistency
              if (difference > 0 && difference <= 0.02) {
                log('[ROUNDING FIX DEBUG] Using cart subtotal for consistency');
                discountedPriceDisplay = cartSubtotal.toFixed(2).replace('.', ',');
              }
            }

            let compareAtPrice;
            if (isSaleItem) {
              compareAtPrice = original
                ? (original.basePrice * cartItem.quantity).toFixed(2).replace('.', ',')
                : null;
            } else {
              compareAtPrice = allocation.originalPrice.toFixed(2).replace('.', ',');
            }

            updateCartItemDOM(cartItem, discountedPriceDisplay, compareAtPrice, true, isSaleItem);
          } catch (error) {
            console.error(`Error processing cart item ${cartItem.id}:`, error);
          }
        });

        return; // Exit after discount processing
      }

      // NO DISCOUNT - QUANTITY UPDATES ONLY (now also handles 0% codes)
      log('[RebuyCartCallbacks] Processing quantity updates without discount');

      cart.items.forEach(cartItem => {
        try {
          log('[RebuyCartCallbacks] Updating item:', cartItem.id, 'quantity:', cartItem.quantity);
          if (parseInt(cartItem.price) === 0) return;

          const isSaleItem =
            cartItem.product?.selected_variant?.compare_at_price &&
            parseFloat(cartItem.product.selected_variant.compare_at_price) >
              parseFloat(cartItem.price) / 100;

          if (originalStates.has(cartItem.id)) {
            revertItemToOriginal(cartItem, isSaleItem);
          } else {
            const { totalSalePrice, totalCompareAtPrice, hasDiscount } = calculatePrices(cartItem);
            updateCartItemDOM(
              cartItem,
              totalSalePrice,
              isSaleItem
                ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                    .toFixed(2)
                    .replace('.', ',')
                : null,
              hasDiscount,
              isSaleItem
            );
          }
        } catch (error) {
          console.error(`Error processing cart item ${cartItem.id}:`, error);
        }
      });
    } catch (error) {
      console.error('Error in processCartItems:', error);
    }
  }

  // Public API
  return {
    init: function () {
      if (!window.RebuyEventManager) {
        console.error('[RebuyCartCallbacks] ERROR: RebuyEventManager is not available!');
        return;
      }

      window.RebuyEventManager.on('cart:change', data => {
        processCartItems(data.cart);
      })
        .on('cart:ready', data => {
          processCartItems(data.cart);
        })
        .on('cart:show', data => {
          processCartItems(data.cart);
        })
        .on('discount:applied', data => {
          processCartItems(data.cart);
        })
        .on('discount:removed', data => {
          processCartItems(data.cart);
        });
    },

    debug: function (enabled) {
      debug = enabled;
      log('Debug logging', enabled ? 'enabled' : 'disabled');
      return this;
    },
  };
})();
